<script setup lang="ts">
import { IonApp, IonRouterOutlet } from '@ionic/vue';
import { App } from '@capacitor/app';
import {
  backupRef,
  initializeFirebaseDB,
  restore,
  restoreOrBackup,
} from './services/FirebaseDB.ts';
import { onMounted, ref } from 'vue';
import NavigationBar from './components/NavigationBar.vue';
import PolicyModal from './components/PolicyModal.vue';
import { useCurrentUser } from 'vuefire';
import { useRoute } from 'vue-router';
import { ensureRef } from './composable/ensureRef.ts';
import { useNetworkStatus, isOnline } from './composable/useNetworkStatus.ts';
import {
  getPolicyAgreement,
  savePolicyAgreement,
  needsPolicyAgreement,
} from './services/PolicyService.ts';

const user = useCurrentUser();
const route = useRoute();
const showPolicyModal = ref(false);

useNetworkStatus();

onMounted(() => {
  ensureRef(user, async () => {
    initializeFirebaseDB();
    if (user.value?.uid) {
      const agreement = await getPolicyAgreement(user.value.uid);
      if (needsPolicyAgreement(agreement)) {
        showPolicyModal.value = true;
      }
    }
  });

  App.addListener('appStateChange', (_) => {
    restoreOrBackup();
  });
});

ensureRef(backupRef, () => {
  restore();
});

async function onPolicyAgreed() {
  if (user.value?.uid) {
    await savePolicyAgreement(user.value.uid);
    showPolicyModal.value = false;
  }
}
</script>

<template>
  <ion-app>
    <PolicyModal v-if="showPolicyModal" @agreed="onPolicyAgreed" />
    <!-- Offline indicator -->
    <div
      v-if="!isOnline"
      class="fixed top-0 right-0 left-0 z-50 border-b-4 border-yellow-500 bg-yellow-100 px-4 pt-[max(0.5rem,env(safe-area-inset-top))] pb-2 text-center text-sm text-yellow-700"
    >
      <span class="flex items-center justify-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="mr-2 h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        You are currently offline. Some features may be limited.
      </span>
    </div>

    <div class="flex min-h-screen flex-col md:flex-row">
      <NavigationBar v-if="user && !route.meta.focusMode" />
      <ion-router-outlet
        :class="
          user && !route.meta.focusMode
            ? '[&>.ion-page]:pb-[calc(5rem_+_env(safe-area-inset-bottom))] [&>.ion-page]:md:pb-0 [&>.ion-page]:md:pl-20'
            : ''
        "
      />
    </div>
  </ion-app>
</template>

<style>
.safe-area {
  padding-top: env(safe-area-inset-top);
  padding-right: env(safe-area-inset-right);
  padding-bottom: env(safe-area-inset-bottom);
  padding-left: env(safe-area-inset-left);
}
</style>
